// Sculpted Endurance cars (sculpt.ts): a front-engined GT coupe and a
// touring hatchback, at real scale. Generic designs, not any manufacturer's.
import * as THREE from 'three'
import { plate, wingElement } from './loft'
import { paintPlanar, type Livery, type PlanarLayout } from './livery'
import { materials, mirror, numberDecal, paintMaterial, wheel } from './parts'
import { sculpt, type BodySpec, type Sculpted } from './sculpt'
import type { CarModel } from './index'

const V = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z)

type SculptedSpec = {
  body: BodySpec
  axles: [number, number]
  track: number
  tyre: { radius: number; front: number; rear: number; rim: number; spokes: number }
  wing: { span: number; chord: number; y: number; z: number; neck: number }
  layout: PlanarLayout
  headlights: { x: number; y: number; z: number; w: number; h: number; tilt: number }
  tail: { y: number; z: number; w: number }
  grille: { y: number; z: number; w: number; h: number }
  mirrors: [number, number, number]
}

const specs: Record<'gt' | 'rookie', SculptedSpec> = {
  gt: {
    body: {
      side: [[2.3, 0.17], [2.36, 0.4], [2.27, 0.6], [1.75, 0.76], [1.0, 0.88], [0.78, 0.93], [0.2, 1.25], [-0.45, 1.28], [-0.95, 1.2], [-1.75, 0.99], [-2.2, 0.93], [-2.36, 0.86], [-2.37, 0.34], [-2.22, 0.16]],
      plan: [[0.48, 2.37], [0.82, 2.22], [0.97, 1.9], [0.98, 1.05], [0.92, 0.5], [0.93, -0.4], [1.0, -0.95], [1.0, -1.85], [0.9, -2.25], [0.55, -2.38]],
      front: [[1.0, 0.12], [1.02, 0.66], [0.97, 0.9], [0.72, 1.17], [0.55, 1.29], [0, 1.31]],
      windows: [
        [[0.82, 0.93], [0.25, 1.235], [-0.38, 1.245], [-0.42, 0.95]],
        [[-0.5, 0.95], [-0.47, 1.24], [-0.9, 1.175], [-1.7, 0.985], [-1.62, 0.95]],
      ],
      arches: [{ z: 1.45, y: 0.35, r: 0.4 }, { z: -1.27, y: 0.35, r: 0.41 }],
      bevel: 0.07,
    },
    axles: [1.45, -1.27],
    track: 0.84,
    tyre: { radius: 0.35, front: 0.3, rear: 0.33, rim: 0.23, spokes: 5 },
    wing: { span: 1.8, chord: 0.33, y: 1.45, z: -2.02, neck: 0.42 },
    layout: { numberZ: 0.05, numberY: 0.6, numberH: 0.38, nameZ: -1.0, nameY: 0.6, nameH: 0.2, beltY: 0.9 },
    headlights: { x: 0.66, y: 0.6, z: 2.12, w: 0.34, h: 0.1, tilt: 0.45 },
    tail: { y: 0.82, z: -2.36, w: 1.5 },
    grille: { y: 0.33, z: 2.355, w: 0.9, h: 0.16 },
    mirrors: [0.98, 0.98, 0.62],
  },
  rookie: {
    body: {
      side: [[2.2, 0.19], [2.27, 0.44], [2.17, 0.7], [1.45, 0.85], [0.98, 0.95], [0.4, 1.37], [-0.6, 1.425], [-1.6, 1.42], [-2.0, 1.34], [-2.16, 1.08], [-2.27, 0.92], [-2.28, 0.32], [-2.12, 0.17]],
      plan: [[0.55, 2.27], [0.86, 2.1], [0.97, 1.8], [0.96, 0.95], [0.92, 0.4], [0.93, -0.8], [0.98, -1.1], [0.98, -1.85], [0.9, -2.17], [0.6, -2.28]],
      front: [[0.98, 0.13], [1.0, 0.72], [0.95, 0.97], [0.76, 1.28], [0.63, 1.42], [0, 1.44]],
      windows: [
        [[0.92, 0.97], [0.42, 1.335], [-0.3, 1.37], [-0.32, 0.98]],
        [[-0.42, 0.98], [-0.4, 1.375], [-1.5, 1.375], [-1.55, 0.98]],
        [[-1.68, 1.0], [-1.66, 1.37], [-1.95, 1.315], [-2.1, 1.06]],
      ],
      arches: [{ z: 1.35, y: 0.33, r: 0.38 }, { z: -1.33, y: 0.33, r: 0.38 }],
      bevel: 0.08,
    },
    axles: [1.35, -1.33],
    track: 0.83,
    tyre: { radius: 0.33, front: 0.27, rear: 0.27, rim: 0.23, spokes: 6 },
    wing: { span: 1.6, chord: 0.28, y: 1.52, z: -2.0, neck: 0.3 },
    layout: { numberZ: -0.35, numberY: 0.62, numberH: 0.38, nameZ: 0.75, nameY: 0.58, nameH: 0.18, beltY: 0.95 },
    headlights: { x: 0.64, y: 0.68, z: 2.12, w: 0.36, h: 0.07, tilt: 0.3 },
    tail: { y: 0.95, z: -2.27, w: 1.45 },
    grille: { y: 0.4, z: 2.265, w: 1.0, h: 0.2 },
    mirrors: [0.97, 1.04, 0.8],
  },
}

const sculptCache = new Map<string, Sculpted>()
function sculpted(type: 'gt' | 'rookie'): Sculpted {
  let s = sculptCache.get(type)
  if (!s) {
    s = sculpt(specs[type].body)
    sculptCache.set(type, s)
  }
  return s
}

export function buildSculpted(type: 'gt' | 'rookie', livery: Livery): CarModel {
  const spec = specs[type]
  const { body, glass, bounds } = sculpted(type)
  const car = new THREE.Group()
  const canvas = document.createElement('canvas')
  paintPlanar(canvas, livery, { zMin: bounds.min.z, zMax: bounds.max.z, yMin: bounds.min.y, yMax: bounds.max.y, xMax: bounds.max.x }, spec.layout)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  tex.anisotropy = 8
  const paint = paintMaterial(null, tex)
  const primary = paintMaterial(livery.primary)
  const secondary = paintMaterial(livery.secondary)
  const lamps = new THREE.MeshBasicMaterial({ color: 0xe9e9e2 })
  const add = (g: THREE.BufferGeometry, m: THREE.Material, x = 0, y = 0, z = 0) => {
    const mesh = new THREE.Mesh(g, m)
    mesh.position.set(x, y, z)
    mesh.castShadow = true
    car.add(mesh)
    return mesh
  }

  add(body, paint)
  add(glass, materials.glass)

  // Wheel well liners (dark), so the arches read as openings.
  for (const z of spec.axles) {
    const liner = add(new THREE.CylinderGeometry(spec.tyre.radius + 0.05, spec.tyre.radius + 0.05, bounds.max.x * 2 - 0.12, 32, 1, true), materials.dark, 0, spec.tyre.radius, z)
    liner.rotation.z = Math.PI / 2
    ;(liner.material as THREE.Material).side = THREE.DoubleSide
  }

  // Splitter, side skirts, diffuser.
  add(new THREE.BoxGeometry(bounds.max.x * 1.9, 0.02, 0.14), materials.carbon, 0, 0.1, bounds.max.z - 0.02)
  for (const s of [1, -1]) add(new THREE.BoxGeometry(0.06, 0.06, (spec.axles[0] - spec.axles[1]) - 0.85), materials.carbon, s * (bounds.max.x - 0.06), 0.13, (spec.axles[0] + spec.axles[1]) / 2)
  add(new THREE.BoxGeometry(bounds.max.x * 1.5, 0.12, 0.22), materials.carbon, 0, 0.17, bounds.min.z + 0.08)
  for (let i = -3; i <= 3; i++) add(new THREE.BoxGeometry(0.015, 0.16, 0.26), materials.carbonMatte, i * 0.2, 0.2, bounds.min.z + 0.06)

  // Grille and intakes, headlights, tail lights.
  const g = spec.grille
  add(new THREE.PlaneGeometry(g.w, g.h), materials.grille, 0, g.y, g.z + 0.012)
  for (const s of [1, -1]) {
    const intake = add(new THREE.PlaneGeometry(0.26, 0.1), materials.grille, s * 0.62, g.y - 0.02, g.z - 0.03)
    intake.rotation.y = s * 0.35
    const h = spec.headlights
    const lamp = add(new THREE.SphereGeometry(1, 24, 12), lamps, s * h.x, h.y, h.z)
    lamp.scale.set(h.w / 2, h.h, 0.16)
    lamp.rotation.set(-h.tilt * 0.3, s * h.tilt, 0)
  }
  const tail = add(new THREE.BoxGeometry(spec.tail.w, 0.06, 0.03), materials.tail, 0, spec.tail.y, spec.tail.z)
  tail.rotation.y = Math.PI

  // Rear wing on swan necks.
  const w = spec.wing
  add(wingElement(w.span, w.chord, 0.05, { camber: 0.1 }), primary, 0, w.y, w.z)
  add(wingElement(w.span, w.chord * 0.45, 0.03, { camber: 0.14 }), secondary, 0, w.y + 0.09, w.z - w.chord * 0.42)
  for (const s of [1, -1]) {
    add(plate([[w.z + 0.2, w.y - 0.12], [w.z + 0.2, w.y + 0.14], [w.z - 0.26, w.y + 0.16], [w.z - 0.26, w.y - 0.12]], 0.016), secondary, s * (w.span / 2 + 0.01), 0, 0)
    add(plate([[w.z - 0.06, w.y + 0.02], [w.z + 0.18, w.y - w.neck], [w.z + 0.34, w.y - w.neck], [w.z + 0.1, w.y + 0.02]], 0.022), materials.carbon, s * 0.42, 0, 0)
  }

  // Mirrors on the doors.
  const [mx, my, mz] = spec.mirrors
  for (const s of [1, -1]) car.add(mirror(primary, V(s * (mx - 0.14), my - 0.04, mz), V(s * mx, my, mz), [0.2, 0.11, 0.1]))

  // Wheels (rims in the accent color).
  const wheels: THREE.Group[] = []
  const t = spec.tyre
  const rimMat = paintMaterial(livery.accent)
  for (const [z, width] of [[spec.axles[0], t.front], [spec.axles[1], t.rear]] as const) {
    for (const side of [1, -1] as const) {
      const { pivot, spin } = wheel({ radius: t.radius, width, rimRadius: t.rim, spokes: t.spokes, band: livery.accent, rimMaterial: rimMat }, side)
      pivot.position.set(side * spec.track, t.radius, z)
      car.add(pivot)
      wheels.push(spin)
    }
  }

  // Number on the bonnet.
  const number = numberDecal(livery.number, 0.36, livery.panel ?? '#F7F7F5')
  number.position.set(0.32, 0.84, 1.35)
  number.rotation.x = -Math.PI / 2 + 0.16
  car.add(number)

  return { group: car, wheels, lamps: [lamps], dispose: () => tex.dispose() }
}
